import {
  TRegisterInput,
  TNotificationTemplate,
  TNotification,
  TNotificationOpenedPayload,
  TPushNotificationPayload,
  TUpdateNotificationPayload,
  ENotificationTypes,
  ENotificationStatus,
  TSendNotificationPayload,
  TUnscheduleNotificationPayload,
  TSimpleMessage,
  TScheduleNotificationPayload,
  TMultiDaySchedule,
  TAuthUserType,
  TPostUser,
  TReschedule,
} from "./types";
import requests from "./requests";
import { multiDaySchedule, reschedule } from "./helpers";
import axios, {
  AxiosHeaderValue,
  AxiosPromise,
  AxiosStatic,
  HeadersDefaults,
} from "axios";
import unscheduledNotifications from "./requests/unscheduledNotifications";
import postUser from "./requests/postUser";
import bulkPostNotifications from "./requests/bulkPostNotifications";

const {
  register,
  getNotifications,
  updateNotifications,
  postNotificationOpened,
  postNotifications,
  scheduleNotifications,
} = requests;

type APIParams = {
  serverURL: string;
  appAxios?: AxiosStatic;
  apiToken?: string;
  headers?: HeadersDefaults & {
    [key: string]: AxiosHeaderValue;
  };
};

type InitializeApiSignature = {
  register: (payload: TRegisterInput) => AxiosPromise<void>;
  getNotifications: () => AxiosPromise<TNotification[]>;
  updateNotifications: (
    payload: TUpdateNotificationPayload[]
  ) => AxiosPromise<void>;
  postNotificationOpened: (
    payload: TNotificationOpenedPayload
  ) => AxiosPromise<void>;
  postNotifications?: (payload: TSendNotificationPayload) => Promise<void>;
  bulkPostNotifications?: (
    payload: TSendNotificationPayload[]
  ) => Promise<void>;
  scheduleNotifications?: (
    payload: TScheduleNotificationPayload
  ) => AxiosPromise<TSimpleMessage>;
  unscheduledNotifications?: (
    notifications: TUnscheduleNotificationPayload
  ) => AxiosPromise<TSimpleMessage>;
  multiDaySchedule?: (
    payload: TMultiDaySchedule
  ) => AxiosPromise<TSimpleMessage>;
  reschedule?: (payload: TReschedule) => AxiosPromise<TSimpleMessage>;
  postUser?: (payload: TPostUser) => AxiosPromise<TAuthUserType>;
};

// Present but rejecting, so an unconfigured caller cannot mistake a no-op for a send.
const unconfigured =
  (method: string) =>
  async (): Promise<never> => {
    throw new Error(
      `client-notifications-sdk: ${method} requires an apiToken, nothing was sent`
    );
  };

const InitializeApi: (payload: APIParams) => InitializeApiSignature = ({
  serverURL,
  apiToken,
  headers,
  appAxios = axios,
}) => {
  appAxios.defaults.headers = {
    ...appAxios.defaults.headers,
    ...headers,
  };

  return {
    register: register(serverURL, appAxios),
    getNotifications: getNotifications(serverURL, appAxios),
    updateNotifications: updateNotifications(serverURL, appAxios),
    postNotificationOpened: postNotificationOpened(serverURL, appAxios),
    ...(apiToken
      ? {
          postNotifications: postNotifications(serverURL, appAxios, apiToken),
          bulkPostNotifications: bulkPostNotifications(
            serverURL,
            appAxios,
            apiToken
          ),
          scheduleNotifications: scheduleNotifications(
            serverURL,
            appAxios,
            apiToken
          ),
          unscheduledNotifications: unscheduledNotifications(
            serverURL,
            appAxios,
            apiToken
          ),
          multiDaySchedule: multiDaySchedule(serverURL, appAxios, apiToken),
          reschedule: reschedule(serverURL, appAxios, apiToken),
          postUser: postUser(serverURL, appAxios, apiToken),
        }
      : {
          postNotifications: unconfigured("postNotifications"),
          bulkPostNotifications: unconfigured("bulkPostNotifications"),
          scheduleNotifications: unconfigured("scheduleNotifications"),
          unscheduledNotifications: unconfigured("unscheduledNotifications"),
          multiDaySchedule: unconfigured("multiDaySchedule"),
          reschedule: unconfigured("reschedule"),
          postUser: unconfigured("postUser"),
        }),
  };
};

export { InitializeApi };
export type {
  TAuthUserType,
  TRegisterInput,
  TNotificationTemplate,
  TNotification,
  TNotificationOpenedPayload,
  TPushNotificationPayload,
  TUpdateNotificationPayload,
  ENotificationTypes,
  ENotificationStatus,
};
